/**
 * Edit or delete a single patient note from a preview surface.
 *
 * Quick notes typed into the Treatment Record toolbar land in `patient_notes` and
 * were, until now, write-once: the Treatment Record rendered them but offered no way
 * to correct a typo or remove one. This is the write path behind that drawer.
 *
 * THE INVOICE MIRROR. Billing writes an invoice's `notes` field into `patient_notes`
 * under a deterministic id (`invoice-note:<invoiceId>`) so it appears on the Treatment
 * Record and the Patient Timeline alongside real notes. Those rows look identical in
 * the preview, but they are a projection, not a source. Editing one as if it were an
 * ordinary note would leave `invoices.notes` stale, and the next invoice save would
 * silently overwrite the edit. So both operations here write through to the invoice —
 * an edit updates `invoices.notes`, a delete clears it — keeping the two in step
 * whichever surface the change is made from.
 *
 * Writes are sequential `db.execute` calls. Both operations touch at most two rows and
 * are ordered so an interruption leaves the invoice, not the note, as the stale one.
 * (PRD.329 correction: this comment used to say only `db.execute` produces CRUD
 * entries — the PRD.329 §9.3 spike showed `db.writeTransaction` does too, uploading as
 * one batch. Not migrated here. Inside a write transaction use only `tx.*`.)
 */

import {
  invoiceIdFromMirrorNote,
  validateNoteBody,
  type NoteBodyValidation,
} from '@helios/data-access';

/** Minimal shape of the PowerSync handle this service needs. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

export interface SaveNoteBodyParams {
  db: Db;
  noteId: string;
  /** Raw text from the editor; trimmed and validated here. */
  body: string;
}

export interface NoteMutationResult {
  ok: boolean;
  error?: string;
}

export async function savePatientNoteBody(params: SaveNoteBodyParams): Promise<NoteMutationResult> {
  const { db, noteId, body } = params;
  if (!noteId) return { ok: false, error: 'No note selected.' };

  const validation: NoteBodyValidation = validateNoteBody(body);
  if (!validation.ok) return { ok: false, error: validation.error };

  const now = new Date().toISOString();
  try {
    // The note first — it is what the user is looking at.
    await db.execute(`UPDATE patient_notes SET body = ?, updated_at = ? WHERE id = ?`, [
      validation.value,
      now,
      noteId,
    ]);

    // Write through to the invoice when this note is a Billing mirror, so the two
    // cannot drift apart. `title`, `note_type`, `alert_level` and `visibility` are
    // deliberately untouched: this editor owns the body and nothing else.
    const invoiceId = invoiceIdFromMirrorNote(noteId);
    if (invoiceId) {
      await db.execute(`UPDATE invoices SET notes = ?, updated_at = ? WHERE id = ?`, [
        validation.value,
        now,
        invoiceId,
      ]);
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not save the note.' };
  }
}

export interface DeleteNoteParams {
  db: Db;
  noteId: string;
}

export async function deletePatientNote(params: DeleteNoteParams): Promise<NoteMutationResult> {
  const { db, noteId } = params;
  if (!noteId) return { ok: false, error: 'No note selected.' };

  try {
    await db.execute(`DELETE FROM patient_notes WHERE id = ?`, [noteId]);

    // Clearing the invoice's notes is what makes the deletion stick: Billing recreates
    // the mirror from `invoices.notes` on every invoice save, so leaving the text there
    // would resurrect the note the moment the invoice is next edited.
    const invoiceId = invoiceIdFromMirrorNote(noteId);
    if (invoiceId) {
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET notes = NULL, updated_at = ? WHERE id = ?`, [
        now,
        invoiceId,
      ]);
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not delete the note.' };
  }
}
